// React Compiler opt-out: the query reads below are against the mutable mandark
// cache and must re-evaluate on every render — compiling this hook would memoize
// them on the (stable) `queries` identity and freeze `isReady` at its first value.
'use no memo';

import {useEffect, useReducer} from 'react';
import {List} from 'immutable';

import {getActiveState} from 'client/framework';

/**
 * @typedef {import('../../albert-io/json-api-framework/request/builder').QueryBuilder} QueryBuilder
 */

/**
 * @typedef {Object} ReturnValue
 * @property {boolean} isReady - Whether or not all queries are populated
 * @property {boolean} hasErrored - Whether any query's request failed. `isResourcePopulated` stays
 *   false after an error, so a caller that gates on `isReady` alone waits forever.
 * @property {*[]} resources - An array of resources in the order that they were passed in
 */

/**
 * @typedef {Object} Options
 * @property {boolean} [skip] - Hold the request. Reading a resource is what dispatches it, so a
 *   caller that cannot yet build the right query (e.g. a feature flag is still resolving) has no
 *   other way to avoid firing the wrong one and refetching. Skipped calls report `isReady: false`
 *   and hand back empty Lists, so this is for list-shaped queries only.
 */

/**
 * Historically this hook had no subscription and relied on the app's global
 * top-down re-render to pick up resource arrival. Compiled (React Compiler)
 * consumers skip those parent-driven re-renders, so the hook now subscribes to
 * the state tree's change event — which mandark request settles reach via
 * CHANGE_CALLBACK → getActiveState().forceUpdate() (see App.react.js) — and
 * re-renders its consumer directly.
 *
 * @param {QueryBuilder[]} queries
 * @param {Options} [options]
 * @returns {ReturnValue}
 */
const useMandarkQueries = (queries, {skip = false} = {}) => {
  const [, forceUpdate] = useReducer((n) => n + 1, 0);

  useEffect(() => {
    const onChange = () => forceUpdate();
    getActiveState().on('change', onChange);
    return () => {
      getActiveState().off('change', onChange);
    };
  }, []);

  // `getResource()` is what registers interest and dispatches the request, so skipping has to
  // short-circuit before it — not filter its result afterwards.
  if (skip) {
    return {isReady: false, hasErrored: false, resources: queries.map(() => List())};
  }

  const isReady = queries.every((query) => query.isResourcePopulated());
  const hasErrored = queries.some((query) => !query.isResourceValid());
  const resources = queries.map((query) => query.getResource());

  return {
    isReady,
    hasErrored,
    resources
  };
};

export default useMandarkQueries;
